<script setup lang="ts">
import { Badge, type BadgeVariants } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowUpRight, BellRing, Ellipsis, Trash2 } from 'lucide-vue-next';
import MessageAttachmentList from './MessageAttachmentList.vue';
import type { MessageAttachment } from '~/types/models';
import { toMillis } from '~/lib/timestamps';

const props = withDefaults(defineProps<{
    direction?: 'incoming' | 'outgoing';
    messageId?: string;
    senderName?: string;
    channelLabel?: string;
    channelVariant?: BadgeVariants['variant'];
    timestamp?: string;
    subject?: string;
    contextNote?: string;
    content: string;
    automated?: boolean;
    actionPath?: string;
    actionLabel?: string;
    inlineActionLabel?: string;
    attachments?: MessageAttachment[];
    canAddAttachmentsToProject?: boolean;
    canManageResponse?: boolean;
    canDelete?: boolean;
    responseRequired?: boolean;
    responseStatus?: 'waiting' | 'responded' | 'resolved';
    responseRequestedAt?: unknown;
    responseActionLoading?: boolean;
}>(), {
    direction: 'incoming',
    messageId: '',
    senderName: 'Unknown',
    channelLabel: '',
    channelVariant: 'outline',
    timestamp: '',
    subject: '',
    contextNote: '',
    automated: false,
    actionPath: '',
    actionLabel: '',
    inlineActionLabel: '',
    attachments: () => [],
    canAddAttachmentsToProject: false,
    canManageResponse: false,
    canDelete: false,
    responseRequired: false,
    responseStatus: undefined,
    responseRequestedAt: undefined,
    responseActionLoading: false,
});

const emit = defineEmits<{
    addAttachmentToProject: [attachment: MessageAttachment];
    markResponseNeeded: [];
    sendResponseReminder: [];
    resolveResponse: [];
    inlineAction: [];
    deleteMessage: [];
}>();

const waitingDays = computed(() => {
    const requestedAt = toMillis(props.responseRequestedAt);
    if (!requestedAt) return 0;
    return Math.max(0, Math.floor((Date.now() - requestedAt) / 86_400_000));
});

function handleBubbleClick(event: MouseEvent) {
    if (!props.inlineActionLabel || (event.target as Element).closest('button, a')) return;
    emit('inlineAction');
}
</script>

<template>
    <div class="flex" :class="direction === 'outgoing' ? 'justify-end' : 'justify-start'">
        <article
            :id="messageId ? `message-${messageId}` : undefined"
            class="max-w-[82%] rounded-2xl px-3 py-2 text-foreground"
            :class="[direction === 'outgoing' ? 'rounded-br-md bg-muted' : 'rounded-bl-md bg-primary/10 dark:bg-primary/15', inlineActionLabel ? 'cursor-pointer' : '']"
            @click="handleBubbleClick"
        >
            <slot name="metadata">
                <div class="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span class="font-medium text-foreground">{{ senderName }}</span>
                    <Badge
                        v-if="channelLabel"
                        :variant="channelVariant"
                        class="h-5 px-1.5 text-[10px] font-medium"
                    >
                        {{ channelLabel }}
                    </Badge>
                    <Badge v-if="automated" variant="secondary" class="h-5 px-1.5 text-[10px] font-medium">
                        Automated
                    </Badge>
                    <span v-if="timestamp" aria-hidden="true">•</span>
                    <span v-if="timestamp">{{ timestamp }}</span>
                </div>
            </slot>

            <p v-if="subject" class="mb-1 text-xs font-medium text-foreground/80">{{ subject }}</p>

            <slot name="context">
                <p v-if="contextNote" class="mb-1 text-[11px] text-muted-foreground">
                    {{ contextNote }}
                </p>
            </slot>

            <slot>
                <p v-if="content" class="whitespace-pre-wrap text-sm">{{ content }}</p>
            </slot>

            <Button v-if="actionPath" as-child size="sm" variant="outline" class="mt-3 h-8 rounded-full px-3">
                <NuxtLink :to="actionPath">
                    {{ actionLabel || 'Open item' }}
                    <ArrowUpRight class="ml-1.5 size-3.5" />
                </NuxtLink>
            </Button>
            <Button v-if="inlineActionLabel" type="button" size="sm" variant="link" class="mt-2 h-auto p-0 text-xs" @click="$emit('inlineAction')">
                {{ inlineActionLabel }}
            </Button>

            <MessageAttachmentList
                :attachments="attachments"
                :can-add-to-project="canAddAttachmentsToProject"
                @add-to-project="$emit('addAttachmentToProject', $event)"
            />

            <div
                v-if="canManageResponse || canDelete || responseRequired || responseStatus === 'responded'"
                class="mt-2 flex flex-wrap items-center justify-end gap-2 border-t border-border/50 pt-2 text-xs"
            >
                <span v-if="responseRequired && responseStatus === 'waiting'" class="mr-auto inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
                    <BellRing class="size-3.5" />
                    Waiting for response{{ waitingDays ? ` · ${waitingDays}d` : '' }}
                </span>
                <span v-else-if="responseStatus === 'responded'" class="mr-auto text-emerald-700 dark:text-emerald-300">Client responded</span>

                <button
                    v-if="responseRequired && responseStatus === 'waiting'"
                    type="button"
                    class="font-medium text-primary hover:underline"
                    :disabled="responseActionLoading"
                    @click="$emit('sendResponseReminder')"
                >
                    {{ responseActionLoading ? 'Sending…' : 'Send reminder' }}
                </button>

                <DropdownMenu v-if="canManageResponse || canDelete">
                    <DropdownMenuTrigger as-child>
                        <Button type="button" variant="ghost" size="icon" class="size-7" :disabled="responseActionLoading" aria-label="Message actions">
                            <Ellipsis class="size-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem v-if="canDelete" variant="destructive" @select="$emit('deleteMessage')">
                            <Trash2 class="size-4" />
                            Delete message
                        </DropdownMenuItem>
                        <DropdownMenuItem v-if="canManageResponse && !responseRequired" @click="$emit('markResponseNeeded')">
                            Mark response needed
                        </DropdownMenuItem>
                        <DropdownMenuItem v-else-if="canManageResponse" @click="$emit('resolveResponse')">
                            Mark as resolved
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </article>
    </div>
</template>
